
import React, { useEffect, useState } from "react";
import {
  FetchOrderViewApi,
  OrderStatusChangeApi,
} from "../../ApiCalls/Orders";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import "jspdf-autotable";
import jsPDF from "jspdf";

const SellerOrderView = () => {
  const { id } = useParams();
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deliveryDateUpdating, setDeliveryDateUpdating] = useState(false);

  // Map for status values
  const statusMap = {
    1: "Order Confirmed",
    2: "Shipping",
    3: "Delivered",
  };

  // Fetch order details on component mount
  useEffect(() => {
    const fetchOrderView = async () => {
      setLoading(true);
      try {
        const response = await FetchOrderViewApi(id);
        setOrderDetails(response.Data[0]?.order_details || {});
        setOrderItems(response.Data[0]?.order_items || []);
      } catch (error) {
        console.error("Error fetching order details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrderView();
  }, [id]);

  const handleDeliveryDateChange = async (orderId, productId, newDate) => {
    const updatedItems = orderItems.map((item) =>
      item.product_id === productId
        ? { ...item, delivery_date: newDate }
        : item
    );
    setOrderItems(updatedItems);
  };

  const handleStatusChange = async (orderId, productId, status) => {
    const updatedItems = orderItems.map((item) =>
      item.product_id === productId
        ? { ...item, status: parseInt(status) }
        : item
    );
    setOrderItems(updatedItems);
  };

  const handleConfirmOrder = async () => {
    setUpdatingStatus(true);
    try {
      for (const product of orderItems) {
        const data = {
          delivery_date: dayjs(product.delivery_date).format("YYYY-MM-DD"),
          status: product.status.toString(),
        };
        await OrderStatusChangeApi(
          orderDetails.order_number,
          product.product_id,
          data
        );
      }
      alert("Order updated successfully!");
    } catch (error) {
      console.error("Error updating order:", error);
      alert("Failed to update the order. Please try again.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(20);
    doc.text("Bricksmix", 10, 10);

    doc.setFontSize(14);
    doc.text(`Order No: ${orderDetails.order_number}`, 10, 20);
    doc.text(
      `Date: ${dayjs(orderDetails.order_date).format("DD-MM-YYYY")}`,
      10,
      30
    );
    doc.text(`Customer Name: ${orderDetails.customer_name}`, 10, 40);
    doc.text(`Address: ${orderDetails.delivery_address}`, 10, 50);

    const header = [
      [
        "Product Code",
        "Product Name",
        "Delivery Date",
        "Quantity",
        "Unit Price",
        "Total",
      ],
    ];
    const data = orderItems.map((item) => [
      item.product_code,
      item.product_name,
      dayjs(item.delivery_date).format("DD-MM-YYYY"),
      item.quantity,
      item.amount,
      (item.quantity * item.amount).toFixed(2),
    ]);

    doc.autoTable({
      head: header,
      body: data,
      startY: 60,
      theme: "grid",
    });

    const totalAmount = orderItems
      .reduce((total, item) => total + item.quantity * item.amount, 0)
      .toFixed(2);
    doc.text(`Total Amount: ${totalAmount}`, 10, doc.lastAutoTable.finalY + 20);

    doc.save(orderDetails.order_number);
  };

  return (
    <div className="h-full w-full space-y-4">
      {loading ? (
        <div className="text-center text-gray-500">Loading...</div>
      ) : (
        <>
          {/* Order Info */}
          <div id="print-invoice-section" className="w-full">
            <div className="grid w-full grid-cols-[1fr,1fr,1fr,3fr] bg-white p-2 font-medium text-brand-button-green-bg">
              <div className="text-center">Order No</div>
              <div className="text-center">Order Date</div>
              <div className="text-center">Customer Name</div>
              <div className="text-center">Customer Address</div>
            </div>
            {orderDetails ? (
              <div className="grid w-full grid-cols-[1fr,1fr,1fr,3fr] gap-2 bg-white p-2">
                <div className="h-fit rounded border p-2 text-center">
                  {orderDetails.order_number}
                </div>
                <div className="h-fit rounded border p-2 text-center">
                  {dayjs(orderDetails.order_date).format("DD-MM-YYYY")}
                </div>
                <div className="h-fit rounded border p-2 text-center">
                  {orderDetails.customer_name}
                </div>
                <div className="rounded border p-2 text-center">
                  {orderDetails.delivery_address}
                </div>
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500">
                No order details available.
              </div>
            )}
          </div>

          {/* Products */}
          <div className="mt-5 w-full">
            <div className="grid w-full grid-cols-[1.5fr,2.5fr,2fr,1fr,2fr,1fr,2fr,1.5fr] bg-white p-2 font-medium text-brand-button-green-bg">
              <div className="text-center">Product Code</div>
              <div className="text-center">Product Name</div>
              <div className="text-center">Delivery Date</div>
              <div className="text-center">Quantity</div>
              <div className="text-center">Amount</div>
              <div className="text-center">Tax</div>
              <div className="text-center">Status</div>
              <div className="text-center">Total Amount</div>
            </div>
            {orderItems.length > 0 ? (
              orderItems.map((product, index) => (
                <div
                  key={index}
                  className="grid w-full grid-cols-[1.5fr,2.5fr,2fr,1fr,2fr,1fr,2fr,1.5fr] gap-2 bg-white p-2 text-sm"
                >
                  <div className="line-clamp-1 h-fit rounded border px-1 py-2 text-center hover:line-clamp-none">
                    {product.product_code}
                  </div>
                  <div className="line-clamp-1 h-fit rounded border px-1 py-2 text-center hover:line-clamp-none">
                    {product.product_name}
                  </div>
                  <div className="line-clamp-1 h-fit rounded border px-1 py-2 text-center hover:line-clamp-none">
                    <input
                      type="date"
                      value={dayjs(product.delivery_date).format("YYYY-MM-DD")}
                      onChange={(e) =>
                        handleDeliveryDateChange(
                          orderDetails.order_number,
                          product.product_id,
                          e.target.value
                        )
                      }
                      className="h-full w-full"
                      disabled={deliveryDateUpdating}
                    />
                  </div>
                  <div className="line-clamp-1 h-fit rounded border px-1 py-2 text-center hover:line-clamp-none">
                    {product.quantity}
                  </div>
                  <div className="line-clamp-1 h-fit rounded border px-1 py-2 text-center hover:line-clamp-none">
                    {product.amount}
                  </div>
                  <div className="line-clamp-1 h-fit rounded border px-1 py-2 text-center hover:line-clamp-none">
                    {product.tax || 0}
                  </div>
                  <div className="line-clamp-1 h-fit rounded border px-1 py-2 text-center hover:line-clamp-none">
                    <select
                      className="h-full w-full"
                      value={product.status}
                      onChange={(e) =>
                        handleStatusChange(
                          orderDetails.order_number,
                          product.product_id,
                          parseInt(e.target.value)
                        )
                      }
                      disabled={updatingStatus}
                    >
                      {Object.entries(statusMap).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="line-clamp-1 h-fit rounded border p-2 text-center hover:line-clamp-none">
                    {(
                      product.quantity *
                      (product.amount + (product.tax || 0))
                    ).toFixed(2)}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-500">
                No products available.
              </div>
            )}
          </div>

          {/* Total Amount and Actions */}
          <div className="text- mt-5 flex w-full items-center justify-end font-semibold text-brand-button-green-bg">
            Total Amount
          </div>
          <div className="flex w-full items-center justify-end bg-white p-4">
            <div className="flex space-x-5">
              <button
                onClick={generatePDF}
                className="rounded bg-blue-500 px-5 py-2 text-white hover:bg-blue-600"
              >
                Print Order
              </button>
              <button
                className={`rounded px-5 py-2 text-white ${
                  updatingStatus
                    ? "bg-gray-400"
                    : "bg-green-500 hover:bg-green-600"
                }`}
                onClick={handleConfirmOrder}
                disabled={updatingStatus}
              >
                {updatingStatus ? "Updating..." : "Confirm"}
              </button>
              <span className="border px-4 py-2 text-black">
                {orderItems
                  .reduce((total, item) => total + item.quantity * item.amount, 0)
                  .toFixed(2)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SellerOrderView;
